/**
 * @file RepOrderRequestsModal.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-30
 * @modified 2026-10-30
 * @description The office's "Rep requests" window, from the top of Sales
 *              Orders (Cam, 29 Oct 2026: "on click open a modal where they can
 *              create sales order from request (log history of actions) which
 *              marks the request as "Opened by X at Y" and on save (order
 *              created) marks as complete (moves to complete tab in modal
 *              where history can be viewed later)").
 *
 *   Open (open + opened, oldest first) and Complete (completed, closed,
 *   withdrawn, newest first). Each request is a card (RepOrderRequestCard).
 *   Acting on one opens it first — "Opened by <you> at <time>", the first
 *   opener kept (POST /:id/open) — then:
 *     order                 Create sales order — Create Customer Order filled in
 *                           from it (utils/repOrderRequestApi hand-off); its
 *                           save completes the request in the same transaction
 *     amend_order           Change the order — Edit Customer Order with the
 *                           rep's request shown above it; its save completes it
 *     standing_order        Create standing order — the standing order window
 *                           filled in; its save completes it
 *     amend_standing_order  Change the standing order — the standing order
 *                           window with the request above it
 *   "Close without an order…" with a required reason. History on every card
 *   (AuditHistoryModal, entity rep_order_request). Each action needs what its
 *   save needs (utils/repOrderRequests OFFICE_PERMISSION) — greyed otherwise.
 */

import React, { useCallback, useEffect, useState } from 'react';
import Modal from './Modal';
import { showAlert } from './Alert';
import Textarea from './Textarea';
import AuditHistoryModal from './AuditHistoryModal';
import StandingOrderModal from './StandingOrderModal';
import RepOrderRequestCard from './RepOrderRequestCard';
import { useAccess } from '../hooks/useAccess';
import { useEntityModal } from './EntityModalProvider';
import { startEdit, EDIT_EVENT } from '../utils/editHandoff';
import { repRequestsApi, handOffRequest } from '../utils/repOrderRequestApi';
import {
  OFFICE_PERMISSION, KIND_WORDS, standingOrderPrefill, requestRef, CLOSE_REASON_MAX, isOpenStatus
} from '../utils/repOrderRequests';
import { API_URL } from '../config';
import './RepOrderRequests.css';

const PERMISSION_WORDS = {
  'orders.create': 'Create sales orders',
  'orders.edit': 'Edit sales orders',
  'standing_orders.edit': 'Edit standing orders'
};

const ACTION_WORDS = {
  order: { icon: 'add_shopping_cart', label: 'Create sales order' },
  amend_order: { icon: 'edit', label: 'Change the order' },
  standing_order: { icon: 'event_repeat', label: 'Create standing order' },
  amend_standing_order: { icon: 'edit_calendar', label: 'Change the standing order' }
};

function RepOrderRequestsModal({ onClose, onNavigate, onChanged }) {
  const { canWrite } = useAccess();
  const { openEntity } = useEntityModal();
  const [tab, setTab] = useState('open');
  const [data, setData] = useState({ loading: true, ready: true, reason: null, requests: [], counts: { open: 0, complete: 0 }, error: null });
  const [busy, setBusy] = useState(null);
  const [closing, setClosing] = useState(null); // { id, reason }
  const [history, setHistory] = useState(null);
  const [standing, setStanding] = useState(null); // { request, prefill } | { request, standingOrder }

  const load = useCallback(() => {
    setData(d => ({ ...d, loading: true }));
    repRequestsApi.list(tab)
      .then(d => {
        setData({ loading: false, ready: d.ready !== false, reason: d.reason, requests: d.requests || [], counts: d.counts || { open: 0, complete: 0 }, error: null });
        if (onChanged) onChanged(d.counts ? d.counts.open : null);
      })
      .catch(e => setData(d => ({ ...d, loading: false, error: e.message })));
  }, [tab, onChanged]);

  useEffect(() => { load(); }, [load]);

  /** Open it (Opened by me at now), then take it to the screen that makes it. */
  const act = async (r) => {
    setBusy(r.id);
    try {
      const { request } = await repRequestsApi.open(r.id);
      if (request.kind === 'order') {
        handOffRequest(request);
        window.dispatchEvent(new CustomEvent(EDIT_EVENT, { detail: { menu: 'create-customer-order' } }));
        onClose();
        if (onNavigate) onNavigate('create-customer-order');
      } else if (request.kind === 'amend_order') {
        handOffRequest(request);
        onClose();
        startEdit('customer_order', request.order_id, onNavigate);
      } else if (request.kind === 'standing_order') {
        setStanding({ request, prefill: standingOrderPrefill(request) });
      } else if (request.kind === 'amend_standing_order') {
        const res = await fetch(`${API_URL}/api/standing-orders/${request.standing_order_id}`, { credentials: 'include' });
        const json = await res.json();
        if (!json.success || !json.standingOrder) throw new Error(json.error || 'That standing order could not be loaded');
        setStanding({ request, standingOrder: json.standingOrder });
      }
      load();
    } catch (e) {
      showAlert(e.message, 'error');
      load();
    } finally {
      setBusy(null);
    }
  };

  const close = async () => {
    if (!closing || !closing.reason.trim()) return;
    setBusy(closing.id);
    try {
      await repRequestsApi.close(closing.id, closing.reason.trim());
      showAlert(`${requestRef(closing.id)} closed without an order`, 'success');
      setClosing(null);
      load();
    } catch (e) {
      showAlert(e.message, 'error');
    } finally {
      setBusy(null);
    }
  };

  const actionsFor = (r) => {
    const perm = OFFICE_PERMISSION[r.kind];
    const allowed = canWrite(perm);
    const words = ACTION_WORDS[r.kind];
    const why = allowed ? null : `Needs the "${PERMISSION_WORDS[perm] || perm}" permission`;
    const buttons = [];
    if (isOpenStatus(r.status)) {
      buttons.push(
        <button key="act" type="button" className="ror-action is-primary" disabled={!allowed || busy === r.id}
          onClick={() => act(r)} title={why || `${words.label} from ${requestRef(r.id)}`}>
          <span className="material-symbols-rounded" aria-hidden="true">{words.icon}</span>
          {busy === r.id ? 'Opening…' : words.label}
        </button>,
        <button key="close" type="button" className="ror-action" disabled={!allowed || busy === r.id}
          onClick={() => setClosing({ id: r.id, reason: '' })} title={why || 'Close it without making an order — say why'}>
          <span className="material-symbols-rounded" aria-hidden="true">block</span>
          Close without an order…
        </button>
      );
    }
    if (r.produced_order_id) {
      buttons.push(
        <button key="order" type="button" className="ror-action" onClick={() => openEntity('customer_order', r.produced_order_id)}>
          <span className="material-symbols-rounded" aria-hidden="true">receipt_long</span>
          {r.produced_order_number || 'The order'}
        </button>
      );
    }
    buttons.push(
      <button key="history" type="button" className="ror-action" onClick={() => setHistory(r)} title="What happened to this request">
        <span className="material-symbols-rounded" aria-hidden="true">history</span>
        History
      </button>
    );
    return (
      <>
        {buttons}
        {closing && closing.id === r.id && (
          <div className="ror-close-reason" style={{ flexBasis: '100%' }}>
            <span className="ror-label">Why is it closed without an order?</span>
            <Textarea
              value={closing.reason}
              onChange={(e) => setClosing({ ...closing, reason: e.target.value })}
              rows={2}
              maxLength={CLOSE_REASON_MAX}
              placeholder="e.g. Ordered by phone as SO-D…, or the customer cancelled"
            />
            <div className="ror-card-actions">
              <button type="button" className="ror-action" onClick={() => setClosing(null)}>Back</button>
              <button type="button" className="ror-action is-primary" disabled={!closing.reason.trim() || busy === r.id} onClick={close}>
                Close it
              </button>
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <>
      <Modal isOpen onClose={onClose} title="Rep requests" size="xl" className="rep-order-requests-office">
        {!data.ready && <div className="ror-not-ready">{data.reason}</div>}
        {data.ready && (
          <>
            <div className="ror-tabs" role="tablist">
              <button type="button" role="tab" aria-selected={tab === 'open'} className={`ror-tab${tab === 'open' ? ' is-on' : ''}`} onClick={() => setTab('open')}>
                Open <span className="ror-tab-count">{data.counts.open}</span>
              </button>
              <button type="button" role="tab" aria-selected={tab === 'complete'} className={`ror-tab${tab === 'complete' ? ' is-on' : ''}`} onClick={() => setTab('complete')}>
                Complete <span className="ror-tab-count">{data.counts.complete}</span>
              </button>
            </div>
            <p className="ror-hint">
              {tab === 'open'
                ? 'Requests from reps, oldest first. Opening one marks it "Opened by" you; saving the order (or standing order) made from it completes it.'
                : 'Done, closed and withdrawn requests, newest first — with their history.'}
            </p>
            {data.error && <div className="ror-error ror-error-block">{data.error}</div>}
            {data.loading && !data.requests.length && <div className="ror-empty">Loading…</div>}
            {!data.loading && !data.requests.length && !data.error && (
              <div className="ror-empty">{tab === 'open' ? 'No requests waiting.' : 'Nothing here yet.'}</div>
            )}
            <div className="ror-office-list">
              {data.requests.map(r => (
                <RepOrderRequestCard
                  key={r.id}
                  request={r}
                  viewer="office"
                  onOpenOrder={(id) => openEntity('customer_order', id)}
                  actions={actionsFor(r)}
                />
              ))}
            </div>
          </>
        )}
      </Modal>

      {history && (
        <AuditHistoryModal
          isOpen
          onClose={() => setHistory(null)}
          entityType="rep_order_request"
          entityId={history.id}
          entityIdentifier={`${requestRef(history.id)} — ${KIND_WORDS[history.kind]}, ${history.account_name || ''}`}
        />
      )}

      {standing && (
        <StandingOrderModal
          standingOrder={standing.standingOrder || null}
          prefill={standing.prefill || null}
          repRequest={standing.request}
          onClose={() => { setStanding(null); load(); }}
          onSave={(saved) => {
            const done = saved && saved.rep_request && saved.rep_request.status === 'completed';
            showAlert(done ? `Saved — ${requestRef(standing.request.id)} is done` : 'Standing order saved', 'success');
            setStanding(null);
            load();
          }}
        />
      )}
    </>
  );
}

export default RepOrderRequestsModal;
